import type { LoyaltyCard } from '@app/api/ExternalLoyalty';
import type { LoyaltyModelRecordInfo } from '@app/api/Loyalty';
import type { ModelPaymentAccount } from '@app/api/PaymentAccounts';
import type { ACQUIRING, BANK, CASH } from '@app/constants';
import type {
  ACCOUNT_CHECKING,
  ACCOUNT_CORRESPONDENT,
  BALANCE,
  BANK as BANK_IN_CAMEL_CASE,
  BANK_BIK,
  BRANCH_ID,
  DESCRIPTION,
  ID,
  IS_DEFAULT,
  IS_DELETED,
  LEGAL_ENTITY,
  LEGAL_ENTITY_ID,
  NAME as NAME_IN_CAMEL_CASE,
  PAY_TYPE,
} from '@app/shared/constants/paymentAccountKeys';
import type { ModelRecordServiceDiscount, NServices } from '@app/types';

import type { IconDefinition } from '@fortawesome/free-solid-svg-icons';
import type { Dispatch, FC, SetStateAction } from 'react';
// export enum PaymentMethodType {
//   CASH = 'cash',
//   BANK = 'bank',
// }

// export enum PaymentDataKey {
//   ID = 'Id',
//   BRANCH_ID = 'BranchId',
//   LEGAL_ENTITY_ID = 'LegalEntityId',
//   NAME = 'Name',
//   DESCRIPTION = 'Description',
//   PAY_TYPE = 'PayType',
//   IS_DELETED = 'IsDeleted',
//   BANK = 'Bank',
//   BANK_BIK = 'BankBik',
//   ACCOUNT_CHECKING = 'AccountChecking',
//   ACCOUNT_CORRESPONDENT = 'AccountCorrespondent',
//   BALANCE = 'Balance',
//   LEGAL_ENTITY = 'LegalEntity',
//   IS_DEFAULT = 'IsDefault',
//   MIGRATE_ID = 'MigrateId',
// }

export type PaymentDataKey =
  | typeof ID
  | typeof ACCOUNT_CHECKING
  | typeof ACCOUNT_CORRESPONDENT
  | typeof BALANCE
  | typeof BANK_IN_CAMEL_CASE
  | typeof BANK_BIK
  | typeof BRANCH_ID
  | typeof DESCRIPTION
  | typeof IS_DEFAULT
  | typeof IS_DELETED
  | typeof LEGAL_ENTITY
  | typeof LEGAL_ENTITY_ID
  | typeof NAME_IN_CAMEL_CASE
  | typeof PAY_TYPE;

export interface SelectedPaymentMethod extends ModelPaymentAccount {
  PayType: typeof CASH | typeof BANK | typeof ACQUIRING;
}

export type PaymentModalProps = {
  isPaymentModalOpen: boolean;
  onOk: (receiptId: string) => Promise<void>;
  onCancel: () => void;
  receiptId: string;
  prepaymentLoader: boolean;
  paymentAmount: string;
  clientBalanceData: {
    Balance: string;
    ObjectId: string;
  };
  totalAmount: number;
  paymentModalType: PaymentModalTypeEnum | null;
  recordDate: string | undefined;
  setPrepaymentLoader: (value: boolean) => void;
  paymentDate: string;
  setPaymentDate: Dispatch<SetStateAction<string>>;
  paymentComment: string;
  setPaymentComment: Dispatch<SetStateAction<string>>;
  paymentMethod: string | null;
  handlePayByYouKassa: (args: {
    Amount: number;
    PaymentMethod: string;
    IsNeedToCreatePayment: boolean;
  }) => void;
};

export type PrepaymentModalProps = {
  isPrepaymentDialogOpen: boolean;
  paymentAmount: string;
  totalAmount: number;
  onCancel: () => void;
  recordDate: string | undefined;
  recordId: number;
  receiptId: string;
  onOk: (
    args: Omit<NServices.ArgsToPrepayRecord, 'activeBranchId'>
  ) => Promise<void>;
};

// Ссылку на оплату по email формирует бэк: GetOrCreateYookassaPayment
// возвращает ConfirmationUrl с одноразовым токеном. Фронт передаёт только
// параметры платежа и показывает пришедшую ссылку (us/10744).
export interface YookassaPaymentLinkParams {
  clientId: number;
  recordId: number;
  amount: number;
  paymentDate: string;
  comment: string;
  paymentMethod: string | null;
  paymentAccountId: number;
  clientPaymentsId: number | null;
}

export interface YookassaPaymentWithEmailModalProps {
  isYookassaPaymentWithEmailModalOpen: boolean;
  setIsYookassaPaymentWithEmailModalOpen: Dispatch<SetStateAction<boolean>>;
  yookassaPaymentLinkParams: YookassaPaymentLinkParams | null;
  setYookassaPaymentLinkParams: Dispatch<
    SetStateAction<YookassaPaymentLinkParams | null>
  >;
  fetchPayments: () => Promise<void>;
  setIsNeedToCreateYooKassaPayment: Dispatch<SetStateAction<boolean>>;
}

export interface YooKassaPaymentProps {
  isModalOpen: boolean;
  clientId: number;
  recordId: number;
  paymentAmount: string;
  paymentDate: string;
  paymentComment: string;
  setPaymentDate: Dispatch<SetStateAction<string>>;
  setPaymentComment: Dispatch<SetStateAction<string>>;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  fetchPayments: () => Promise<void>;
  handledYooKassaPaymentId: number | null;
  receiptId: string;
  yooKassaPaymentMethodCode: string | null;
  isNeedToCreateYooKassaPayment: boolean;
}

export type GeneratePaymentTooltipTitle = (
  selectedPaymentMethod: SelectedPaymentMethod | null,
  dataToRender: Array<{
    key: PaymentDataKey;
    label: string;
  }>
) => JSX.Element;

export interface ServiceTableRow {
  key: number | string;
  service: string;
  count: number;
  price: number;
  discount: number;
  cost: number;
  paid: number;
  total: number;
  discounts: ModelRecordServiceDiscount[];
  isExcludedFromLoyaltyDiscount: boolean;
}

export interface ServicesTableProps {
  data: Array<ServiceTableRow>;
  isPrintPdfLoading: boolean;
  printPdfFile(): Promise<void>;
}

export interface PaymentButtonOptions {
  key: string;
  label: JSX.Element | string | null;
  default: boolean;
}

export enum PaymentTypeEnum {
  CASH = 'CASH',
  CARD = 'CARD',
  BANK = 'BANK',
  BALANCE = 'BALANCE',
  PREPAYMENT = 'PREPAYMENT',
}

export enum PaymentModalTypeEnum {
  CASH = 'CASH',
  CARD = 'CARD',
  BANK = 'BANK',
  BALANCE = 'BALANCE',
  PREPAYMENT = 'PREPAYMENT',
  YOO_KASSA = 'YOO_KASSA',
}

export interface PaymentButtonProps {
  disabled?: boolean;
  overlayClassName?: string;
  className?: string;
  options: PaymentButtonOptions[];
  onSelectMenuItem(key: string): void;
  selectedPaymentMethod: SelectedPaymentMethod | null;
  handleOptionClick: (receipt?: SelectedPaymentMethod | null) => void;
  tooltipTitle?: JSX.Element;
  icon: IconDefinition;
  iconClassName: string;
  title?: string;
  description?: string;
  testId: string;
  dropdownTestId: string;
}

export interface PaymentButtonConfig extends PaymentButtonProps {
  key: string;
}

interface PaymentsProps {
  selectedAmount: string;
  onChangeAmount: (value: string) => void;
  paymentButtonsOptions: PaymentButtonConfig[];
  isPayFormBalanceButtonDisabled: boolean;
  handleOptionClick: (
    modalType: PaymentModalTypeEnum
  ) => ({ receipt, yooKassaPaymentMethodCode }: HandleOptionClickProps) => void;
  Balance: number;
  acquiringPaymentMethods: SelectedPaymentMethod[];
  isPayFormYooKassaButtonDisabled: boolean;
}

export type PaymentsType = FC<PaymentsProps>;
export type PaymentButtonType = FC<PaymentButtonProps>;
export type ServicesTableType = FC<ServicesTableProps>;

export interface LoyaltyCardsProps {
  loyaltyCards: LoyaltyCard[];
  cardBonusesInfo: LoyaltyModelRecordInfo | null;
  setLoyaltyCards: Dispatch<SetStateAction<LoyaltyCard[]>>;
  recordId: number;
  servicesTableData: ServiceTableRow[];
  getRecordInfoHandler: () => Promise<LoyaltyModelRecordInfo | undefined>;
  totalPaid: number;
  initTable: () => Promise<void>;
  fetchPayments: () => Promise<void>;
  calculateBonusesToEarn: (cardId: string) => Promise<void>;
  setCurrentCard: Dispatch<SetStateAction<LoyaltyCard | null>>;
}

export interface CardItemProps {
  recordId: number;
  card: LoyaltyCard;
  servicesTableData: ServiceTableRow[];
  bonusesToExceed?: number;
  getRecordInfoHandler: () => Promise<LoyaltyModelRecordInfo | undefined>;
  totalPaid: number;
  appliedCardId: string | null | undefined;
  initTable: () => Promise<void>;
  fetchPayments: () => Promise<void>;
}

export interface LoyaltyCardTabProps {
  card: LoyaltyCard;
  cardBonusesInfo: LoyaltyModelRecordInfo | null;
  recordId: number;
  servicesTableData: ServiceTableRow[];
  getRecordInfoHandler: () => Promise<LoyaltyModelRecordInfo | undefined>;
  totalPaid: number;
  initTable: () => Promise<void>;
  fetchPayments: () => Promise<void>;
  calculateBonusesToEarn: (cardId: string) => Promise<void>;
  activeKey: string | null | undefined;
}

export interface PayWithBonusesModalProps {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  availableBonuses: number;
  cardId: string;
  cardNumber: string;
  recordId: number;
  servicesTableData: ServiceTableRow[];
  bonusesToExceed?: number;
  getRecordInfoHandler: () => Promise<LoyaltyModelRecordInfo | undefined>;
  initTable: () => Promise<void>;
  fetchPayments: () => Promise<void>;
}
export interface ConfirmCancelPaymentWithBonusesModalProps {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  recordId: number;
  getRecordInfoHandler: () => Promise<LoyaltyModelRecordInfo | undefined>;
  initTable: () => Promise<void>;
  fetchPayments: () => Promise<void>;
}
export interface ApplyPercentageDiscountProps {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  percentageDiscount: number;
  cardId: string;
  recordId: number;
  cardNumber: string;
  initTable: () => Promise<void>;
  getRecordInfoHandler: () => Promise<LoyaltyModelRecordInfo | undefined>;
  fetchPayments: () => Promise<void>;
}

export interface AddNewLoyaltyCardModalProps {
  open: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  setLoyaltyCards: Dispatch<SetStateAction<LoyaltyCard[]>>;
}

export interface AcquiringPaymentButtonProps {
  method: SelectedPaymentMethod;
  isPayFormYooKassaButtonDisabled: boolean;
  handleOptionClick: (
    modalType: PaymentModalTypeEnum
  ) => ({ receipt, yooKassaPaymentMethodCode }: HandleOptionClickProps) => void;
}

export interface HandleOptionClickProps {
  receipt?: SelectedPaymentMethod | null;
  yooKassaPaymentMethodCode?: string;
}
